/* eslint camelcase: off */
import { find, pickBy } from "@utils/lodash";
import { REPORTS_SETTINGS } from "@constants";
import AbstractService from "./AbstractService";

export default class ReportService extends AbstractService {
	getReportsByUserType = async userParams =>
		this.get({
			endpoint: "reports/getreportsbyusertype",
			queryParams: {
				organizationId: userParams.Organization.Id,
				userTypeId: userParams.Organization.OrganizationTypeId,
				allOrgReports: false,
			},
		});

	static getEmbeddedReportById(reportId, organizationId) {
		return this.post({
			endpoint: "reports/embeddashboardlink",
			queryParams: {
				organizationId,
				reportId,
			},
		});
	}

	static createReport(data) {
		return this.post({
			endpoint: "reports",
			data,
		});
	}

	static updateReport(data) {
		return this.put({
			endpoint: "reports/",
			data,
		});
	}

	static deleteReport(id) {
		return this.del({
			endpoint: `reports/${id}`,
		});
	}

	static changeReportTier(organizationId, userTypeId, tierName, tierNumber) {
		return this.post({
			endpoint: "reports/changereporttier",
			queryParams: {
				organizationId,
				userTypeId,
				tierName,
				tierNumber,
			},
		});
	}

	static getReportSettingByType = type => find(REPORTS_SETTINGS, object => object.type === type);

	static client_overview(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);
		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				includeAll: false,
				clientId: filters.clientId,
				clientSupervisorContactId: filters.clientSupervisorContactId,
				startDateRange: filters.startDateRange,
				endDateRange: filters.endDateRange,
			},
		});
	}

	static project_overview(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);
		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				includeAll: false,
				projectId: filters.projectId,
				clientSupervisorContactId: filters.clientSupervisorContactId,
				locationId: filters.locationId,
				startDateRange: filters.startDateRange,
				endDateRange: filters.endDateRange,
			},
		});
	}

	static officer_overview(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);
		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				officerId: filters.officer.id,
				startDateRange: filters.startDateRange,
				endDateRange: filters.endDateRange,
			},
		});
	}

	static location_overview(filters, reportType) {
		return this.project_overview(filters, reportType);
	}

	static jurisdiction_overview(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);
		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				jurisdictionId: filters.jurisdictionId,
				startDateRange: filters.startDateRange,
				endDateRange: filters.endDateRange,
			},
		});
	}

	static agency_overview(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);
		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				includeAll: false,
				agencyOrganizationId: filters.agencyId,
				startDateRange: filters.startDateRange,
				endDateRange: filters.endDateRange,
			},
		});
	}

	static agency_overview_detailed(filters, reportType) {
		return this.agency_overview(filters, reportType);
	}

	static coordinator_overview(filters, reportType) {
		return this.agency_overview(filters, reportType);
	}

	static payment_history(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);
		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				officerId: filters.officer.id,
				agencyId: filters.agencyId,
				clientId: filters.clientId,
				projectId: filters.projectId,
				locationId: filters.locationId,
				fromDate: filters.startDateRange,
				toDate: filters.endDateRange,
			},
		});
	}

	static invoice_report(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);
		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				officerId: filters.officer.id,
				agencyId: filters.agencyId,
				clientId: filters.clientId,
				projectId: filters.projectId,
				locationId: filters.locationId,
				invoiceStatus: filters.invoiceStatus,
				fromDate: filters.startDateRange,
				toDate: filters.endDateRange,
				isPayCodeEnabled: filters.isPayCodeEnabled,
			},
		});
	}

	static invoice_aging(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);
		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				clientId: filters.clientId,
				organizationId: filters.agencyId,
			},
		});
	}

	static department_invoice_report(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);
		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				officerId: filters.officer.id,
				clientId: filters.clientId,
				projectId: filters.projectId,
				locationId: filters.locationId,
				fromDate: filters.startDateRange,
				toDate: filters.endDateRange,
			},
		});
	}

	static fairness(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);

		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				jurisdictionId: filters.jurisdictionId,
				startDate: filters.startDateRange,
				endDate: filters.endDateRange,
			},
		});
	}

	static calloff(filters, reportType) {
		const reportSettings = this.getReportSettingByType(reportType);

		return this.get({
			endpoint: reportSettings.endpoint,
			queryParams: {
				startDateRange: filters.startDateRange,
				endDateRange: filters.endDateRange,
			},
		});
	}

	static manual_invoice(reportType, queryParams) {
		const { endpoint } = this.getReportSettingByType(reportType);
		return this.get({
			endpoint,
			queryParams,
		});
	}

	static manual_aging(reportType, queryParams) {
		const { endpoint } = this.getReportSettingByType(reportType);
		return this.get({
			endpoint,
			queryParams,
		});
	}

	static applied_payments(reportType, queryParams) {
		const { endpoint } = this.getReportSettingByType(reportType);
		return this.get({
			endpoint,
			queryParams,
		});
	}

	static exportManualInvoicingReport(fileName, data) {
		return this.post({
			endpoint: "reports/export/csv",
			queryParams: { fileName },
			data,
		});
	}

	static payroll_report(queryParams, reportType) {
		const { endpoint } = this.getReportSettingByType(reportType);
		return this.get({
			endpoint,
			queryParams,
		});
	}

	static deposit(reportType, queryParams) {
		const { endpoint } = this.getReportSettingByType(reportType);
		return this.get({
			endpoint,
			queryParams,
		});
	}

	static exportReport({ reportType, type, locationName, filters, model: data }) {
		const reportSettings = this.getReportSettingByType(reportType);

		if (reportSettings.pdfEndpoint || reportSettings.csvEndpoint) {
			const validParams = [
				"agencyId",
				"clientId",
				"projectId",
				"locationId",
				"clientSupervisorContactId",
				"invoiceStatus",
				"isPayCodeEnabled",
			];
			let queryParams = pickBy(
				filters,
				(value, key) => validParams.includes(key) && value !== "",
			);

			if (reportType === "payroll_report") {
				queryParams = {
					payCodeName: filters.payCodeName,
					organizationId: filters.organizationId,
					isPayCodeEnabled: filters.isPayCodeEnabled,
					startDateRange: filters.startDateRange,
					endDateRange: filters.endDateRange,
				};
			} else {
				queryParams = {
					...queryParams,
					officerId: filters.officer.id,
					fromDate: filters.startDateRange,
					toDate: filters.endDateRange,
				};
			}

			return this.get({
				endpoint: `${reportSettings.endpoint}/${type === "xls" ? "csv" : type}`,
				queryParams,
				responseType: "blob",
			});
		}

		return this.post({
			endpoint: `${reportSettings.fileEndpoint || reportSettings.endpoint}/exportreport`,
			queryParams: {
				type,
				locationName,
				detailed: reportSettings.detailed,
			},
			data,
		});
	}
}
